/**
 * Dev21 — shared UI primitives.
 *
 * Small, reusable building blocks every section composes from: scroll-reveal
 * wrappers, the section shell, and the eyebrow/heading pair. Keeping them here
 * means the sections stay declarative and visually consistent.
 */

import React from 'react';
import { motion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import { DEV21_EASE } from '../theme';

// ── Reveal ─────────────────────────────────────────────────────────────────
/** Fades + lifts its children into view once, when scrolled to. */
export function Reveal({
  children,
  className,
  delay = 0,
  y = 36,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  y?: number;
  /** Allows `<Reveal>` to be used directly inside `.map()`. */
  key?: React.Key;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: DEV21_EASE }}
    >
      {children}
    </motion.div>
  );
}

// ── Section shell ──────────────────────────────────────────────────────────
/** Full-width section with consistent vertical rhythm + responsive padding. */
export function Section({
  id,
  children,
  className,
  tone = 'cream',
}: {
  id: string;
  children: React.ReactNode;
  className?: string;
  /** Background tone — drives the scroll rhythm between sections. */
  tone?: 'cream' | 'surface' | 'ink';
}) {
  const toneClass =
    tone === 'ink'
      ? 'bg-[var(--d21-ink)] text-[var(--d21-cream)]'
      : tone === 'surface'
        ? 'bg-[var(--d21-surface)] text-[var(--d21-ink)]'
        : 'bg-[var(--d21-cream)] text-[var(--d21-ink)]';

  return (
    <section
      id={id}
      className={cn(
        'relative w-full px-5 py-20 sm:px-8 sm:py-24 lg:px-12 lg:py-32',
        toneClass,
        className,
      )}
    >
      <div className="mx-auto w-full max-w-6xl">{children}</div>
    </section>
  );
}

// ── Section heading ────────────────────────────────────────────────────────
/** Eyebrow label + display heading + optional intro paragraph. */
export function SectionHeading({
  label,
  title,
  intro,
  align = 'center',
  invert = false,
}: {
  label: string;
  title: string;
  intro?: string;
  align?: 'center' | 'left';
  invert?: boolean;
}) {
  const alignClass =
    align === 'center' ? 'items-center text-center' : 'items-start text-left';
  return (
    <Reveal className={cn('flex flex-col gap-4', alignClass)}>
      <span className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-[var(--d21-accent)]">
        <span className="h-px w-6 bg-[var(--d21-accent)]" />
        {label}
      </span>
      <h2
        className={cn(
          'font-serif text-[clamp(2rem,5vw,3.4rem)] font-semibold leading-[1.08]',
          invert ? 'text-[var(--d21-cream)]' : 'text-[var(--d21-ink)]',
        )}
      >
        {title}
      </h2>
      {intro ? (
        <p
          className={cn(
            'max-w-2xl text-base leading-relaxed sm:text-lg',
            invert ? 'text-[var(--d21-cream)]/70' : 'text-[var(--d21-muted)]',
          )}
        >
          {intro}
        </p>
      ) : null}
    </Reveal>
  );
}

// ── Glass card ─────────────────────────────────────────────────────────────
/** Frosted, softly-shadowed surface — the template's signature card style. */
export function GlassCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-3xl border border-[var(--d21-border)] bg-white/70 shadow-[0_24px_60px_-32px_rgba(33,26,19,0.45)] backdrop-blur-md',
        className,
      )}
    >
      {children}
    </div>
  );
}
